// store/offlineStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface OfflineSale {
    id: string;
    tenantId: string;
    branchId: string;
    cartData: {
        items: Array<{
            productId: string;
            variantId?: string;
            uomId: string;
            quantity: number;
            unitPrice: number;
            discountAmount: number;
        }>;
        customerId?: string;
        tableId?: string;
        saleType: string;
    };
    payments: Array<{
        paymentMethod: string;
        amount: number;
        reference?: string;
        bankAccountId?: string;
        mobileMoneyProvider?: string;
    }>;
    discountTotal: number;
    grandTotal: number;
    orderNumber: string;
    queuedAt: string;
    retryCount: number;
    lastError?: string;
}

interface OfflineState {
    queue: OfflineSale[];
    isSyncing: boolean;
    lastSyncTime: string | null;
    syncErrors: number;

    // Actions
    addToQueue: (sale: Omit<OfflineSale, 'id' | 'queuedAt' | 'retryCount'>) => void;
    removeFromQueue: (id: string) => void;
    updateRetry: (id: string, error?: string) => void;
    setSyncing: (syncing: boolean) => void;
    setLastSyncTime: (time: string) => void;
    clearQueue: () => void;
    getQueueCount: () => number;
    getPendingAmount: () => number;
}

export const useOfflineStore = create<OfflineState>()(
    persist(
        (set, get) => ({
            queue: [],
            isSyncing: false,
            lastSyncTime: null,
            syncErrors: 0,

            addToQueue: (sale) => {
                const offlineSale: OfflineSale = {
                    ...sale,
                    id: `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    queuedAt: new Date().toISOString(),
                    retryCount: 0,
                };
                set((state) => ({
                    queue: [...state.queue, offlineSale],
                }));

                // Show notification
                const event = new CustomEvent('offline-sale-queued', {
                    detail: { orderNumber: sale.orderNumber }
                });
                window.dispatchEvent(event);
            },

            removeFromQueue: (id) => {
                set((state) => ({
                    queue: state.queue.filter((s) => s.id !== id),
                }));
            },

            updateRetry: (id, error) => {
                set((state) => ({
                    queue: state.queue.map((s) =>
                        s.id === id
                            ? { ...s, retryCount: s.retryCount + 1, lastError: error }
                            : s
                    ),
                }));
            },

            setSyncing: (syncing) => set({ isSyncing: syncing }),

            setLastSyncTime: (time) => set({ lastSyncTime: time }),

            clearQueue: () => set({ queue: [], syncErrors: 0 }),

            getQueueCount: () => get().queue.length,

            getPendingAmount: () =>
                get().queue.reduce((sum, s) => sum + s.grandTotal, 0),
        }),
        {
            name: 'nyan-offline-queue',
            partialize: (state) => ({ queue: state.queue, lastSyncTime: state.lastSyncTime }),
        }
    )
);